import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Slider,
  Stack,
  Typography,
  Dialog,
  DialogContent,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect } from 'react';
import AvatarEditor from 'react-avatar-editor';
import { useTranslation } from 'react-i18next';

import FileUploadIcon from '../../assets/svgs/FileUploadIcon.svg';
import { Locales, LocalesFullName } from '../../i18n';
import Button from '../Button';

/**
 * TODO: Add "name" to represent the name of the file included in the input field
 * @param onSubmitFile
 * @param title
 * @param content
 * @param isFormSubmit
 * @param pictureName
 * @param removePicture - Deprecated: use onSubmitFile instead with null value
 * @param accept
 * @param skipModalStep
 * @param cropWidth
 * @param cropHeight
 * @param language
 * @param isRequired
 * @return {JSX.Element}
 * @constructor
 */
export const InputFile = ({
  onSubmitFile,
  title,
  content,
  isFormSubmit,
  pictureName,
  removePicture,
  accept,
  skipModalStep,
  cropWidth,
  cropHeight,
  language,
  isRequired,
}) => {
  const editorRef = useRef(null);
  const inputRef = useRef(null);
  const [t] = useTranslation();

  const [file, setFile] = useState(null);
  const [border, setBorder] = useState('');
  const [scale, setScale] = useState(1);
  const [minScale, setMinScale] = useState(1);
  const [image, setImage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [pdfFileUrl, setPdfFileUrl] = useState('');

  useEffect(() => {
    if (isFormSubmit && !image && isRequired) {
      setBorder('red');
    }
  }, [isFormSubmit, image, isRequired]);

  useEffect(() => {
    if (pictureName && !image) {
      if (skipModalStep) {
        setPdfFileUrl(pictureName);
      } else {
        setImage(pictureName);
      }
    }
  }, [pictureName, image, skipModalStep]);

  const handleFile = (event) => {
    if (event.target.files[0]) {
      let file = event.target.files[0];
      setFile(file);

      if (skipModalStep) {
        onSubmitFile({
          blob: file,
          name: `${Date.now()}${file.name}`,
          type: file.type,
        });
        const url = URL.createObjectURL(file);
        setPdfFileUrl(url);
      } else {
        // Read image dimensions to calculate the minimum zoom scale
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
          // react-avatar-editor scales the image so that at scale=1 it fills
          // the canvas height (cover by height). The effective display scale is:
          //   baseScale = cropHeight / img.height
          // The displayed width of the image at scale=1 is therefore:
          //   displayedWidth = img.width * baseScale
          // For the entire image to fit within the canvas width:
          //   displayedWidth * minScale <= cropWidth
          //   => minScale = cropWidth / displayedWidth
          //                = cropWidth / (img.width * (cropHeight / img.height))
          //                = (cropWidth / cropHeight) * (img.height / img.width)
          const calculated =
            (cropWidth / cropHeight) * (img.height / img.width);
          // If the image is already narrower than the canvas (calculated > 1),
          // 1 remains the sensible minimum.
          const newMinScale = Math.min(1, calculated);
          setMinScale(newMinScale);
          setScale(newMinScale);
          URL.revokeObjectURL(url);
        };
        img.src = url;
        setShowModal(true);
      }
    }
  };

  const handleEditedFile = async () => {
    const sourceCanvas = editorRef.current.getImageScaledToCanvas();

    // Composite the logo onto a white background to fill any transparent areas
    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = sourceCanvas.width;
    outputCanvas.height = sourceCanvas.height;
    const ctx = outputCanvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, outputCanvas.width, outputCanvas.height);
    ctx.drawImage(sourceCanvas, 0, 0);

    const dataUrl = outputCanvas.toDataURL('image/png');
    const blob = await fetch(dataUrl).then((res) => res.blob());
    const url = URL.createObjectURL(blob);
    setImage(url);
    onSubmitFile({
      blob,
      name: `${Date.now()}${file.name}`,
      type: file.type,
    });
    setShowModal(false);
    setBorder('none');
  };

  const handleScale = (event) => {
    setScale(Number(event.target.value));
  };

  const closeModal = () => {
    inputRef.current.value = null;
    setShowModal(false);
  };

  const removeImage = () => {
    setFile(null);
    setImage(null);
    onSubmitFile(null);
    removePicture();
    if (skipModalStep) {
      setPdfFileUrl('');
    }
    inputRef.current.value = null;
  };

  const handleBorder = (color) => () => {
    setBorder(color);
  };

  const getInputHeight = () => {
    switch (content) {
      case 'logo':
        return '85px';
      case 'product':
      case 'pdf':
      default:
        return '69px';
    }
  };

  const getBorderColor = () => {
    if (border === 'red') return '#d95959';
    if (border === 'green') return '#89c959';
    return '#e3e3e3';
  };

  return (
    <>
      <Box
        sx={{
          width: '240px',
          minWidth: '240px',
          height: getInputHeight(),
          border: `1px solid ${getBorderColor()}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <Box
          component="input"
          type="file"
          accept={accept}
          onChange={handleFile}
          onDragOver={handleBorder('green')}
          onDragLeave={handleBorder('none')}
          onDrop={handleBorder('none')}
          ref={inputRef}
          sx={{
            width: '100%',
            height: '100%',
            color: 'transparent',
            position: 'absolute',
            zIndex: 1,
            cursor: 'pointer',
            '&::-webkit-file-upload-button': {
              visibility: 'hidden',
            },
          }}
        />
        <Box
          sx={{
            background: `url(${FileUploadIcon})`,
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            width: '45px',
            height: '45px',
            marginRight: '20px',
            flexShrink: 0,
          }}
        />
        <Stack
          direction="column"
          justifyContent="center"
          alignItems="center"
          sx={{ textAlign: 'center', width: '143px' }}
        >
          <Typography className="title-section">{title}</Typography>
          {language ? (
            <Typography className="text-small-1">
              Drag & Drop | {LocalesFullName[Locales.DE][language]}
            </Typography>
          ) : (
            <Typography className="text-small-1">Drag & Drop</Typography>
          )}
        </Stack>
        {(image || pdfFileUrl) && (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              zIndex: 2,
              backgroundColor: '#fff',
            }}
          >
            <Box
              component="img"
              src={image}
              alt=""
              sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
            {pdfFileUrl && (
              <Typography
                className="title-section"
                onClick={() => window.open(pdfFileUrl, 'blank')}
                sx={{
                  position: 'absolute',
                  cursor: 'pointer',
                  width: '150px',
                  left: 'calc(50% - 75px)',
                  top: 'calc(50% - 10px)',
                  textAlign: 'center',
                }}
              >
                PDF Öffnen
              </Typography>
            )}
            <CloseIcon
              onClick={removeImage}
              sx={{
                backgroundColor: 'yellow.main',
                top: 0,
                right: 0,
                position: 'absolute',
                zIndex: 3,
                cursor: 'pointer',
              }}
              alt=""
            />
          </Box>
        )}
      </Box>
      <Dialog
        open={showModal}
        onClose={closeModal}
        PaperProps={{
          sx: {
            width: '704px',
            maxWidth: '704px',
            padding: '33px 32px 0 32px',
          },
        }}
      >
        <DialogContent
          sx={{
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <AvatarEditor
            ref={editorRef}
            image={file}
            width={cropWidth}
            height={cropHeight}
            border={50}
            color={[0, 0, 0, 0.75]}
            scale={scale}
            rotate={0}
            allowScaleLessThan1
          />
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ marginTop: '33px', marginBottom: '30px', width: '100%' }}
          >
            <Slider
              value={scale}
              min={minScale}
              max={3}
              step={0.01}
              onChange={handleScale}
              sx={{ marginLeft: '10px', width: '60%' }}
            />
            <Button size="medium" variant="main" onClick={handleEditedFile}>
              {t('speichern')}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};

InputFile.defaultProps = {
  removePicture: () => {},
  skipModalStep: false,
  cropWidth: 539,
  cropHeight: 160,
  language: '',
  isRequired: true,
};

InputFile.propTypes = {
  /** It is a required function property that handles the event when a file is submitted */
  onSubmitFile: PropTypes.func.isRequired,
  /** It is a required string property that represents the title or label for the input file component */
  title: PropTypes.string.isRequired,
  /**It is a required string property that represents the content or description of the input file component  */
  content: PropTypes.string.isRequired,
  /**  It is an optional string property that represents the name of the picture file */
  pictureName: PropTypes.string,
  /**It is an optional function property that handles the event when the picture is removed  */
  removePicture: PropTypes.func,
  /** It is an optional string property that specifies the file types or formats that are accepted by the input file component */
  accept: PropTypes.string,
  /**It is an optional boolean property that determines whether to skip a modal step in the process */
  skipModalStep: PropTypes.bool,
  /** It is an optional string property that represents the specific language for the component */
  language: PropTypes.string,
  /** It is an optional boolean property that indicates whether the input file component is required or not */
  isRequired: PropTypes.bool,
};

export default InputFile;
